import React, { useEffect, useMemo, useState } from 'react';
import { rh as api } from '../services/api';
import { eur, montant, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, SelecteurUnite, basculer } from './ventesUtils';
import { sequentielle, Echelle } from './heatmap';
import './Ventes.css';

// ─── RH — Analyse croisée (Auto Antilles) ───────────────────────────────────
// Matrice dimension × mois sur la paie agrégée d'A97 DATA (même réponse que
// RH — Masse salariale, pivotée à l'écran : 160 lignes, rien à recalculer
// côté serveur). Même gabarit qu'APV — Analyse croisée : barre Mesure /
// Dimension, échelle de couleur, lignes qui filtrent.
//
// Dimensions : société de paie, concession groupée (dépliable sur ses
// activités VN, VO, APV, PR, Admin), activité. Une dimension ne
// se filtre pas elle-même (CLAUDE.md §4 bis, r. 4) : ses lignes restent
// toutes affichées, la valeur choisie est surlignée.
//
// Les ratios (productivité, poids salarial) ont besoin d'une activité : celle
// de la ligne en dimension Activité, sinon celle choisie dans le panneau. Sans
// activité, leurs boutons sont désactivés — un bouton sans effet se lit comme
// une panne. Toujours sur les seuls mois de paie importés.

const ACTS = ['VN', 'VO', 'APV', 'PR', 'ADMIN'];
const ACT_LIB = { VN: 'VN', VO: 'VO', APV: 'APV', PR: 'PR', ADMIN: 'Admin' };
const CHOIX_ACT = [{ code: '', nom: 'Toutes' }, ...ACTS.map((a) => ({ code: a, nom: ACT_LIB[a] }))];
const MESURES = [
  { code: 'cout', label: 'Coût employeur', m: true },
  { code: 'effectif', label: 'Effectif' },
  { code: 'moyen', label: 'Coût moyen / salarié', m: true, unit: true },
  { code: 'brutFixe', label: 'Brut fixe', m: true },
  { code: 'variable', label: 'Variable', m: true },
  { code: 'partVar', label: 'Part variable', pct: true },
  { code: 'primes', label: 'Primes annuelles', m: true },
  // Exceptionnel (rubriques de paie « 5- » de Finance), seul, par dimension.
  { code: 'exceptionnel', label: 'Exceptionnel', m: true },
  { code: 'cotisations', label: 'Cotisations', m: true },
  { code: 'tauxCharges', label: 'Taux de charges', pct: true },
  { code: 'nonSoumis', label: 'Non soumis', m: true },
  { code: 'prod', label: 'Productivité', ratio: true },
  { code: 'poids', label: 'Poids salarial', ratio: true },
];
const DIMS = [
  { code: 'concession', label: 'Société' },
  { code: 'groupe', label: 'Concessions groupées' },
  { code: 'activite', label: 'Activité' },
];
// Définition affichée des ratios, selon l'activité.
const RATIO_LIB = {
  prod: { VN: 'VN vendus / salarié / mois', VO: 'VO vendus / salarié / mois', APV: 'CA M.O. / salarié / mois', PR: 'CA pièces / salarié / mois' },
  poids: { VN: 'coût par VN vendu', VO: 'coût par VO vendu', APV: 'coût / CA M.O.', PR: 'coût / CA pièces' },
};
const somme = (a) => a.reduce((x, y) => x + y, 0);

export default function RhAnalyse() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('cout');
  const [dimension, setDimension] = useState('groupe');
  const [unite, setUnite] = useState('k');
  const [ouverts, setOuverts] = useState(() => new Set());
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const activite = f.params.get('activite') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.masseSalariale({ periode: f.periode, mois: f.moisStr })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.periode, f.moisStr]);

  const groupes = useMemo(() => data?.groupes || [], [data]);
  const entites = useMemo(() => groupes.flatMap((g) => g.entites.map((e) => ({ ...e, groupe: g }))), [groupes]);
  // Sociétés de paie retenues par le panneau (le GIE suit tout CENTRAUTODIS).
  const retenues = useMemo(() => entites.filter((e) => (e.societes.length
    ? e.societes.some((c) => f.selected.includes(c))
    : e.groupe.societes.every((c) => f.selected.includes(c)))).map((e) => e.entite), [entites, f.selected]);

  const Panneau = (
    <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
      extra={{ choix: { titre: 'Activité', valeurs: CHOIX_ACT, valeur: activite, onChoisir: (c) => f.setFiltre({ activite: c }) } }} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const mois = data.moisPaie;
  const nbMois = mois.length;
  const mesObj = MESURES.find((m) => m.code === mesure);
  // Activité des ratios : celle de la ligne (dimension Activité) ou du panneau.
  // En concessions groupées, ce sont les lignes d'activité (dépliées) qui les portent.
  const ratioDispo = dimension !== 'concession' || ['VN', 'VO', 'APV', 'PR'].includes(activite);
  const mes = mesObj.ratio && !ratioDispo ? 'cout' : mesure;
  const mesCourante = MESURES.find((m) => m.code === mes);

  // ── Une ligne de matrice = un ensemble de sociétés × un ensemble d'activités ──
  const valeur = (ents, acts, idx) => {
    const ms = idx.map((i) => mois[i]);
    const ls = data.lignes.filter((l) => ents.includes(l.entite) && acts.includes(l.activite) && ms.includes(l.ym));
    const t = { cout: 0, effMois: 0, brut: 0, brutFixe: 0, variable: 0, primes: 0, exceptionnel: 0, cotisations: 0, nonSoumis: 0 };
    ls.forEach((l) => { t.cout += l.cout; t.effMois += l.effectif; t.brut += l.brut; t.brutFixe += l.brutFixe; t.variable += l.variable; t.primes += l.primes; t.exceptionnel += l.exceptionnel || 0; t.cotisations += l.cotisations; t.nonSoumis += l.nonSoumis; });
    if (mes === 'effectif') return idx.length ? t.effMois / idx.length : null;   // moyenne sur les mois
    if (mes === 'moyen') return t.effMois ? t.cout / t.effMois : null;
    if (mes === 'partVar') return t.brut ? (t.variable / t.brut) * 100 : null;
    if (mes === 'tauxCharges') return t.brut ? (t.cotisations / t.brut) * 100 : null;
    if (mes === 'prod' || mes === 'poids') {
      const act = acts.length === 1 ? acts[0] : null;
      if (!act || act === 'ADMIN') return null;
      const a = data.activite.filter((x) => ents.includes(x.entite) && ms.includes(x.ym));
      const den = somme(a.map((x) => (act === 'VN' ? x.vn : act === 'VO' ? x.vo : act === 'APV' ? x.caMo : x.caPieces)));
      if (!den) return null;
      if (mes === 'prod') return t.effMois ? den / t.effMois : null;
      return act === 'VN' || act === 'VO' ? t.cout / den : (t.cout / den) * 100;
    }
    return t[mes] || 0;
  };
  // Format selon la mesure — et, pour les ratios, selon l'activité de la ligne.
  const fmt = (v, acts) => {
    if (v == null) return '—';
    if (mes === 'effectif') return v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
    if (mesCourante.pct) return pct1(v);
    if (mes === 'moyen') return eur(v);
    if (mes === 'prod') { const a = acts.length === 1 ? acts[0] : ''; return a === 'VN' || a === 'VO' ? v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : eur(v); }
    if (mes === 'poids') { const a = acts.length === 1 ? acts[0] : ''; return a === 'VN' || a === 'VO' ? eur(v) : pct1(v); }
    return montant(v, unite);
  };

  // ── Lignes selon la dimension ──
  // Le panneau Sociétés vaut pour TOUTES les dimensions (2026-10-13 : il ne
  // filtrait que la vue Activité, Société et Concessions groupées affichaient
  // tout le monde quoi qu'on coche).
  const actsFiltre = activite ? [activite] : ACTS;
  const tous = mois.map((_, i) => i);
  let lignes = [];
  if (dimension === 'activite') {
    lignes = ACTS.map((a) => ({ cle: a, nom: ACT_LIB[a], ents: retenues, acts: [a], filtre: () => f.setFiltre({ activite: basculer(activite, a) }), actif: activite === a }));
  } else if (dimension === 'concession') {
    lignes = entites.filter((e) => retenues.includes(e.entite)).map((e) => ({
      cle: e.entite, nom: e.nom, ents: [e.entite], acts: actsFiltre,
      filtre: e.societes.length ? () => f.setFiltre({ societes: basculer(f.societesUrl, e.societes.join(',')) }) : null,
      actif: e.societes.length && f.societesUrl === e.societes.join(','),
    }));
  } else {
    // Concession groupée ▸ ses activités (VN, VO, APV…) : le détail d'un groupe
    // se lit par service, pas par société. Les trois groupes sont dépliables,
    // Blue compris, pour rester au même niveau. Une ligne d'activité porte une
    // seule activité : productivité et poids salarial s'y calculent.
    groupes.forEach((g) => {
      const ents = g.entites.map((e) => e.entite).filter((e) => retenues.includes(e));
      if (!ents.length) return;
      const on = ouverts.has(g.groupe);
      lignes.push({ cle: g.groupe, nom: g.groupe, ents, acts: actsFiltre, groupe: true, on,
        filtre: () => setOuverts((o) => { const x = new Set(o); x.has(g.groupe) ? x.delete(g.groupe) : x.add(g.groupe); return x; }) });
      if (on) actsFiltre.forEach((a) => lignes.push({ cle: `${g.groupe}/${a}`, nom: ACT_LIB[a], ents, acts: [a], sub: true }));
    });
  }

  // Échelle : toutes les cellules de la mesure courante.
  const vals = [];
  lignes.forEach((o) => tous.forEach((i) => { const v = valeur(o.ents, o.acts, [i]); if (v != null) vals.push(v); }));
  const st = { max: Math.max(...vals, 1) };
  // Pied : les sociétés et activités de la vue courante.
  const entsPied = dimension === 'activite' ? retenues : [...new Set(lignes.filter((l) => !l.sub).flatMap((l) => l.ents))];
  const actsPied = dimension === 'activite' ? ACTS : actsFiltre;
  const piedRatio = (mes === 'prod' || mes === 'poids') && actsPied.length !== 1;
  const dimObj = DIMS.find((d) => d.code === dimension);
  const ratioLib = mesCourante.ratio ? (activite ? RATIO_LIB[mes][activite] : dimension === 'groupe' ? 'déplier une concession pour le ratio de chaque activité' : 'selon l\'activité de la ligne') : null;

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>RH — Analyse croisée</h1></div>
          <div className="vt-asof"><b>{data.periode}</b>{nbMois ? <><br /><span>paie {moisCourt(mois[0])} → {moisCourt(mois[nbMois - 1])} {mois[nbMois - 1].slice(0, 4)}</span></> : null}</div>
        </div>

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg vt-seg-multi">{MESURES.map((m) => {
              const off = m.ratio && !ratioDispo;
              return (
                <button key={m.code} disabled={off} className={mes === m.code ? 'on' : ''}
                  title={off ? 'Choisissez une activité (VN, VO, APV ou PR) dans les filtres, ou la dimension Activité ou Concessions groupées' : ''}
                  onClick={() => setMesure(m.code)}>{m.label}</button>
              );
            })}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => (
              <button key={d.code} className={dimension === d.code ? 'on' : ''} onClick={() => setDimension(d.code)}>{d.label}</button>
            ))}</span>
          </span>
        </div>

        {!nbMois ? (
          <div className="bi-empty"><h2>Aucune paie importée sur cette période</h2><p>Dernier mois de paie importé dans Finance : {data.dernierMoisImporte ? `${moisCourt(data.dernierMoisImporte)} ${data.dernierMoisImporte.slice(0, 4)}` : '—'}.</p></div>
        ) : !groupes.length ? (
          <div className="bi-empty"><h2>Aucune masse salariale dans votre périmètre</h2><p>La paie apparaît ici pour les concessions dont toutes les sociétés sont dans votre habilitation.</p></div>
        ) : (
          <div className="vt-card">
            <div className="vt-card-h">
              <h2>{mesCourante.label} — par {dimObj.label.toLowerCase()} et par mois{activite && dimension !== 'activite' ? ` · ${ACT_LIB[activite]}` : ''}</h2>
              <div className="st-tools">
                <span className="hint">
                  {[ratioLib, mes === 'effectif' && 'total = effectif moyen', mes === 'cout' && 'coût employeur = brut + cotisations patronales'].filter(Boolean).join(' · ')}
                </span>
                {mesCourante.m && !mesCourante.unit && <SelecteurUnite unite={unite} setUnite={setUnite} />}
              </div>
            </div>
            <div className="vt-card-b vt-tbl-wrap">
              <table className="vt-mx tbl-dense">
                <thead><tr><th className="l">{dimObj.label}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th className="tot">{mesCourante.m && !mesCourante.unit ? 'Total' : 'Période'}</th></tr></thead>
                <tbody>
                  {lignes.map((o) => (
                    <tr key={o.cle} className={`${o.sub ? 'sub' : ''}${o.filtre ? ' tbl-clic' : ''}${o.actif ? ' actif' : ''}`} onClick={o.filtre || undefined}>
                      <td className={`l${o.groupe ? (o.on ? ' open' : '') : ' nodrill'}`}>
                        {o.groupe ? <span className="chev">▸</span> : null}{o.nom}
                      </td>
                      {tous.map((i) => {
                        const v = valeur(o.ents, o.acts, [i]); const c = sequentielle(v, st.max);
                        return <td key={i} style={{ background: c.bg }}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, o.acts)}</span></td>;
                      })}
                      <td className="tot">{fmt(valeur(o.ents, o.acts, tous), o.acts)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td className="l">Total</td>
                  {tous.map((i) => <td key={i}>{piedRatio ? '—' : fmt(valeur(entsPied, actsPied, [i]), actsPied)}</td>)}
                  <td className="tot">{piedRatio ? '—' : fmt(valeur(entsPied, actsPied, tous), actsPied)}</td>
                </tr></tfoot>
              </table>
              <div className="vt-lgd"><span>Faible</span><Echelle /><span>Élevé</span></div>
            </div>
          </div>
        )}
      </main>
      {Panneau}
    </>
  );
}
